'use client';

import React, { useState } from 'react';
import { businessData } from '@/config/businessData';

/**
 * ContactMapSection - Shows contact options and interactive Google Maps location.
 */
const ContactMapSection = () => {
  const [isInteractive, setIsInteractive] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [activeMap, setActiveMap] = useState('india');

  const handleOpenMap = () => {
    setIsInteractive(true);
    setIsLoadingMap(true);
  };

  return (
    <section className="w-full mx-auto mt-16 mb-20">
      <div className="bg-[#EEF2F7] rounded-2xl w-full max-w-[1300px] mx-auto p-6 md:p-8">
        <div className="flex flex-col lg:flex-row items-stretch gap-8">
          
          {/* Contact Info Column */}
          <div className="w-full lg:w-[40%] px-2 md:px-3 lg:px-4 py-2 flex flex-col justify-center">
            <h2 className="text-[20px] md:text-[22px] font-semibold text-[#112140] mb-6">Need Help?</h2>

            {/* Phone Section */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                <span className="bg-[#D6E3F8] rounded-md p-2 flex items-center justify-center">
                  <svg width="20" height="20" fill="none">
                    <path
                      d="M7.5 4.5H9.75L10.5 7.5L9 8.25C9.75 10.125 11.375 11.75 13.25 12.5L14 11L17 11.75V14C17 14.6627 16.6627 15 16 15C11.5817 15 8 11.4183 8 7C8 6.33726 8.33726 6 9 6H11.25"
                      stroke="#112140"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="font-semibold text-[#112140] text-[16px]">Phone</span>
              </div>
              <p className="text-[#475467] text-[14px] mb-1">Monday – Saturday: 9:30 AM to 6:30 PM IST</p>
              <a href={`tel:${businessData.contact.phoneLink}`} className="text-[#1A5AFF] text-[16px] font-semibold inline-block hover:underline">{businessData.contact.phoneDisplay}</a>
            </div>

            {/* Email Section */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                <span className="bg-[#D6E3F8] rounded-md p-2 flex items-center justify-center">
                  <svg width="20" height="20" fill="none">
                    <path
                      d="M4 6.5V17.5C4 18.0523 4.44772 18.5 5 18.5H19C19.5523 18.5 20 18.0523 20 17.5V6.5M4 6.5L12 13.5L20 6.5"
                      stroke="#112140"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="font-semibold text-[#112140] text-[16px]">Email Us</span>
              </div>
              <p className="text-[#475467] text-[14px] mb-1">Our friendly team is here to help.</p>
              <a href={`mailto:${businessData.contact.email}`} className="text-[#1A5AFF] text-[16px] font-semibold mb-1 inline-block hover:underline">{businessData.contact.email}</a>
            </div>

            {/* Office Section */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="bg-[#D6E3F8] rounded-md p-2 flex items-center justify-center">
                  <svg width="20" height="20" fill="none">
                    <path
                      d="M12 19C12 19 7 15.5 7 11.5C7 8.46243 9.46243 6 12.5 6C15.5376 6 18 8.46243 18 11.5C18 15.5 13 19 13 19H12Z"
                      stroke="#112140"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="font-semibold text-[#112140] text-[16px]">Office Locations</span>
              </div>
              <div className="flex flex-col gap-4 pl-1">
                <div>
                  <h4 className="text-[12px] font-bold text-[#475467] uppercase tracking-wider mb-0.5">India Headquarters</h4>
                  <a href={businessData.address.mapsLink} target="_blank" rel="noopener noreferrer" className="text-[#1A5AFF] text-[15px] font-semibold leading-relaxed hover:underline inline-block">
                    {businessData.address.line1}<br />
                    {businessData.address.line2} {businessData.address.country}
                  </a>
                </div>
                <div>
                  <h4 className="text-[12px] font-bold text-[#475467] uppercase tracking-wider mb-0.5">Sydney Branch Office</h4>
                  <a href={businessData.sydneyAddress.mapsLink} target="_blank" rel="noopener noreferrer" className="text-[#1A5AFF] text-[15px] font-semibold leading-relaxed hover:underline inline-block">
                    {businessData.sydneyAddress.line1}<br />
                    {businessData.sydneyAddress.line2} {businessData.sydneyAddress.country}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Map Column */}
          <div className="w-full lg:w-[60%] flex flex-col justify-center">
            {/* Map Switcher Tabs */}
            <div className="flex border-b border-[#D6E3F8] gap-4 mb-3">
              <button 
                onClick={() => { setActiveMap('india'); setIsInteractive(false); }} 
                className={`pb-2 text-sm font-semibold border-b-2 transition-all ${activeMap === 'india' ? 'border-[#1A5AFF] text-[#1A5AFF]' : 'border-transparent text-[#475467] hover:text-[#112140]'}`}
              >
                India HQ Map
              </button>
              <button 
                onClick={() => { setActiveMap('sydney'); setIsInteractive(false); }} 
                className={`pb-2 text-sm font-semibold border-b-2 transition-all ${activeMap === 'sydney' ? 'border-[#1A5AFF] text-[#1A5AFF]' : 'border-transparent text-[#475467] hover:text-[#112140]'}`}
              >
                Sydney Map
              </button>
            </div>

            <div className="relative w-full h-[340px] md:h-[400px] rounded-2xl overflow-hidden border border-[#E4E7EC] bg-[#050B14] shadow-[0px_6px_20px_rgba(17,33,64,0.12)] group">
              {!isInteractive ? (
                <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center p-6 bg-[#0B1528] overflow-hidden">
                  {/* Styled Premium Map Fallback Image */}
                  <img
                    src="/images/map-fallback.png"
                    alt="Royal Prime Labels Location Map"
                    className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:scale-105 transition-transform duration-[1500ms] ease-out z-0"
                  />
                  
                  {/* Glassmorphic Dark Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/95 via-[#050B14]/40 to-transparent group-hover:bg-[#050B14]/50 transition-colors duration-500 z-0"></div>
                  
                  {/* Floating Action Content */}
                  <div className="relative z-10 flex flex-col items-center gap-4 text-center pointer-events-auto">
                    <button
                      type="button"
                      onClick={handleOpenMap}
                      className="px-6 py-3 bg-[#C9A84C] hover:bg-white text-slate-950 font-bold rounded-full shadow-lg shadow-[#C9A84C]/20 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-2.5 cursor-pointer relative z-20"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                        <line x1="8" y1="2" x2="8" y2="18"></line>
                        <line x1="16" y1="6" x2="16" y2="22"></line>
                      </svg>
                      Open Live Google Map
                    </button>
                    
                    <a
                      href={activeMap === 'india' ? businessData.address.mapsLink : businessData.sydneyAddress.mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white backdrop-blur-md font-semibold rounded-full border border-white/20 text-sm hover:scale-105 transition-all duration-300 flex items-center gap-2 relative z-20"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 11 12 14 22 4"></polyline>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                      </svg>
                      Get Driving Directions
                    </a>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full relative bg-[#050B14]">
                  {isLoadingMap && (
                    <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center bg-[#0B1528] text-white z-20">
                      {/* Premium Spinner */}
                      <div className="w-10 h-10 border-4 border-[#C9A84C] border-t-transparent rounded-full animate-spin mb-3"></div>
                      <span className="text-sm font-semibold tracking-wide text-slate-300">Loading Live Google Map...</span>
                    </div>
                  )}
                  
                  <iframe
                    src={activeMap === 'india' 
                      ? "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3489.9678191246733!2d77.0844206!3d28.9292316!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390daef33894555f%3A0x72414be7599124c1!2sROYAL%20PRIME%20LABELS!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                      : businessData.sydneyAddress.embedUrl
                    }
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen={true}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    onLoad={() => setIsLoadingMap(false)}
                    className="w-full h-full relative z-10"
                  ></iframe>
                  
                  {/* Minimize Button */}
                  <button
                    type="button"
                    onClick={() => setIsInteractive(false)}
                    className="absolute top-4 right-4 bg-slate-900/90 hover:bg-slate-900 text-white backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-bold shadow-md flex items-center gap-1.5 border border-white/10 hover:scale-105 transition-all z-30"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                    </svg>
                    Minimize Map
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactMapSection;
